import { initials } from "../misc/initials";
import { difference, union } from "lodash";
import {
  type Identifier,
  RecordContextProvider,
  RecordRepresentation,
  useListContext,
  useRecordContext,
  useTimeout,
  useTranslate,
} from "ra-core";
import { type MouseEvent, useCallback, useRef } from "react";
import { Link } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Mail, RotateCcw } from "lucide-react";

import { Status } from "../misc/Status";
import { cn } from "@/lib/utils";
import { EmptyState } from "../shared/EmptyState";
import { Sparkline } from "../shared/Sparkline";
import type { Contact, Sale } from "../types";
import { Avatar } from "./Avatar";
import {
  AcquisitionBadge,
  NextActionBadge,
  PopulationBadge,
  TemperatureBadge,
} from "./ContactBadges";

import { SearchX } from "lucide-react";

// Sprint 3 T3.2 — Colonnes Population / Température / Prochaine action.
// Badges extraits dans ContactBadges.tsx (Sprint 4 T4.4.a) pour réutilisation.

const GRID_COLS =
  "grid-cols-[40px_1fr_140px_60px] lg:grid-cols-[40px_1fr_110px_95px_105px_95px_80px_105px_85px_60px]";

// Reprise 07/09/2026 — Palier de chaleur IA (contacts.heat_tier, dérivé de
// heat_score 0-100). Le badge vit ici plutôt que dans ContactBadges.tsx pour
// rester dans le périmètre du chantier « surface prospection » ; la file
// d'appel (call-queue/) le réimporte depuis ce module.
const HEAT_TIER_PILL: Record<string, { label: string; className: string }> = {
  brulant: {
    label: "Brûlant",
    className: "bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-300",
  },
  chaud: {
    label: "Chaud",
    className:
      "bg-orange-50 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300",
  },
  tiede: {
    label: "Tiède",
    className:
      "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
  },
  froid: {
    label: "Froid",
    className:
      "bg-blue-50 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300",
  },
  mort: { label: "Mort", className: "bg-muted text-muted-foreground" },
};

export const HeatTierBadge = ({
  tier,
  score,
}: {
  tier: string | null | undefined;
  score?: number | null;
}) => {
  const config = tier ? HEAT_TIER_PILL[tier] : undefined;
  if (!config) {
    return <span className="text-muted-foreground/60 text-xs">—</span>;
  }
  return (
    <span
      className={cn(
        "inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium",
        config.className,
      )}
      title={score != null ? `Heat score IA : ${score}/100` : undefined}
    >
      {config.label}
    </span>
  );
};

export const ContactListContent = () => {
  const translate = useTranslate();
  const {
    data: contacts,
    error,
    isPending,
    onToggleItem,
    onSelect,
    selectedIds,
    filterValues,
    setFilters,
  } = useListContext<Contact>();
  const lastSelected = useRef<Identifier | null>(null);
  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  const handleToggleItem = useCallback(
    (id: Identifier, event: MouseEvent) => {
      if (!contacts) return;
      const ids = contacts.map((c) => c.id);
      const lastIdx = lastSelected.current
        ? ids.indexOf(lastSelected.current)
        : -1;
      if (event.shiftKey && lastIdx !== -1) {
        const idx = ids.indexOf(id);
        const range = ids.slice(
          Math.min(lastIdx, idx),
          Math.max(lastIdx, idx) + 1,
        );
        const isSelected = selectedIds?.includes(id);
        onSelect?.(
          isSelected
            ? difference(selectedIds, range)
            : union(selectedIds, range),
        );
      } else {
        onToggleItem(id);
      }
      lastSelected.current = id;
    },
    [contacts, selectedIds, onSelect, onToggleItem],
  );

  if (isPending) return <Skeleton className="w-full h-9" />;
  if (error) return null;

  return (
    <div>
      {/* Table header — Sprint 3 T3.2 :
         - status legacy retiré (Décision 1 du brief)
         - dernière activité retirée (Option B grid)
         - Population / Température / Prochaine action ajoutées en lg: */}
      <div
        className={cn(
          "grid items-center px-2 py-2 border-b text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
          GRID_COLS,
        )}
      >
        <div />
        <div>{translate("resources.contacts.name", { smart_count: 1 })}</div>
        <div>{translate("resources.companies.name", { smart_count: 1 })}</div>
        <div className="hidden lg:block">Population</div>
        <div className="hidden lg:block">Acquisition</div>
        <div className="hidden lg:block">Température</div>
        <div className="hidden lg:block">Chaleur IA</div>
        <div className="hidden lg:block">Prochaine action</div>
        <div className="hidden lg:block">Activité 30j</div>
        <div>
          {translate("resources.contacts.fields.sales_id", {
            _: "Resp.",
          })}
        </div>
      </div>

      {/* Table body */}
      {contacts.map((contact) => (
        <RecordContextProvider key={contact.id} value={contact}>
          <ContactRow contact={contact} handleToggleItem={handleToggleItem} />
        </RecordContextProvider>
      ))}

      {contacts.length === 0 && hasFilters && (
        <EmptyState
          icon={<SearchX className="size-6" />}
          title="Aucun contact ne correspond aux filtres"
          description="Modifie ou réinitialise les filtres pour voir d'autres résultats."
          ctas={
            <Button
              type="button"
              variant="outline"
              className="gap-1.5"
              onClick={() => setFilters?.({}, [])}
            >
              <RotateCcw className="size-3.5" />
              Réinitialiser les filtres
            </Button>
          }
        />
      )}
      {contacts.length === 0 && !hasFilters && (
        <div className="p-4 text-muted-foreground">
          {translate("resources.contacts.empty.title", {})}
        </div>
      )}
    </div>
  );
};

const ContactRow = ({
  contact,
  handleToggleItem,
}: {
  // heat_tier / heat_score sont exposés par contacts_summary mais pas (encore)
  // portés par le type Contact — même contournement que MyDayCompaniesToClose.
  contact: Contact & { heat_tier?: string | null; heat_score?: number | null };
  handleToggleItem: (id: Identifier, event: MouseEvent) => void;
}) => {
  const { selectedIds } = useListContext<Contact>();

  return (
    <div
      className={cn(
        "grid items-center px-2 py-2.5 border-b border-border/50 hover:bg-muted/50 transition-colors",
        GRID_COLS,
      )}
    >
      {/* Checkbox */}
      <div
        className="flex items-center justify-center cursor-pointer"
        onClick={(e) => handleToggleItem(contact.id, e)}
      >
        <Checkbox
          className="cursor-pointer"
          checked={selectedIds.includes(contact.id)}
        />
      </div>

      {/* Contact name + email (Sprint 7 T7.2.b — cf maquette
          contacts-app.jsx : .ct-contact avec .nm + .em). TagsList retiré
          du sous-titre, accessible via popover/fiche. */}
      <Link
        to={`/contacts/${contact.id}/show`}
        className="flex items-center gap-3 min-w-0"
      >
        <Avatar width={25} height={25} />
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">
            {contact.first_name} {contact.last_name ?? ""}
          </div>
          {contact.email_jsonb?.[0]?.email && (
            <div className="text-xs text-muted-foreground truncate">
              {contact.email_jsonb[0].email}
            </div>
          )}
        </div>
      </Link>

      {/* Company */}
      <div className="text-sm text-muted-foreground truncate">
        {contact.company_id != null && (
          <ReferenceField
            source="company_id"
            reference="companies"
            link={false}
          >
            <TextField source="name" />
          </ReferenceField>
        )}
        {contact.company_id == null && "—"}
      </div>

      {/* Population (lg+) */}
      <div className="hidden lg:block">
        <PopulationBadge value={contact.population} />
      </div>

      {/* Acquisition (lg+) */}
      <div className="hidden lg:block">
        <AcquisitionBadge value={contact.acquisition_source} />
      </div>

      {/* Temperature (lg+) */}
      <div className="hidden lg:block">
        <TemperatureBadge value={contact.temperature} />
      </div>

      {/* Chaleur IA (lg+) — Reprise 07/09/2026 : heat_tier est calculé et
          exposé par contacts_summary depuis des semaines mais n'était affiché
          dans aucune liste. */}
      <div className="hidden lg:block">
        <HeatTierBadge tier={contact.heat_tier} score={contact.heat_score} />
      </div>

      {/* Next action (lg+) */}
      <div className="hidden lg:block min-w-0">
        <NextActionBadge
          type={contact.next_action_type}
          date={contact.next_action_date}
        />
      </div>

      {/* Activity sparkline 30j (lg+) — Sprint 9.5 */}
      <div className="hidden lg:flex items-center">
        {contact.last_activity_sparkline?.length ? (
          <Sparkline
            data={contact.last_activity_sparkline}
            width={84}
            height={26}
          />
        ) : (
          <span className="text-fabrik-text-3 text-xs">—</span>
        )}
      </div>

      {/* Sales / Responsible */}
      <div className="flex items-center justify-center">
        {contact.sales_id != null && (
          <ReferenceField source="sales_id" reference="sales" link={false}>
            <SalesAvatar />
          </ReferenceField>
        )}
      </div>
    </div>
  );
};

const SalesAvatar = () => {
  const sale = useRecordContext<Sale>();
  if (!sale) return null;
  return (
    <div
      className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-semibold"
      title={`${sale.first_name ?? ""} ${sale.last_name ?? ""}`}
    >
      {initials(sale)}
    </div>
  );
};

/* ============ MOBILE ============ */

export const ContactListContentMobile = () => {
  const translate = useTranslate();
  const {
    data: contacts,
    error,
    isPending,
    refetch,
  } = useListContext<Contact>();
  const oneSecondHasPassed = useTimeout(1000);

  if (isPending) {
    if (!oneSecondHasPassed) return null;
    return (
      <>
        {[...Array(5)].map((_, index) => (
          <div
            key={index}
            className="flex flex-row items-center py-2 hover:bg-muted transition-colors"
          >
            <div className="flex flex-row gap-4 items-center mr-4">
              <Skeleton className="w-10 h-10 rounded-full" />
            </div>
            <div className="flex-1 min-w-0">
              <Skeleton className="w-32 h-5 mb-2" />
              <Skeleton className="w-48 h-4" />
            </div>
          </div>
        ))}
      </>
    );
  }

  if (error && !contacts) {
    return (
      <div className="p-4">
        <div className="text-center text-muted-foreground mb-4">
          {translate("resources.contacts.list.error_loading")}
        </div>
        <div className="text-center mt-2">
          <Button onClick={() => refetch()}>
            <RotateCcw />
            {translate("crm.common.retry")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="md:divide-y">
      {contacts.map((contact) => (
        <RecordContextProvider key={contact.id} value={contact}>
          <ContactItemContentMobile contact={contact} />
        </RecordContextProvider>
      ))}
      {contacts.length === 0 && (
        <div className="p-4 text-muted-foreground">
          {translate("resources.contacts.empty.title")}
        </div>
      )}
    </div>
  );
};

const ContactItemContentMobile = ({ contact }: { contact: Contact }) => {
  const translate = useTranslate();
  return (
    <Link
      to={`/contacts/${contact.id}/show`}
      className="flex flex-row gap-4 items-center py-2 hover:bg-muted transition-colors"
    >
      <Avatar />
      <div className="flex flex-col grow justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex justify-between">
            <div className="font-medium">
              <RecordRepresentation />
            </div>
            <Status status={contact.status} />
          </div>
          <div className="text-sm text-muted-foreground">
            <span>
              {contact.title && contact.company_id != null
                ? `${translate("resources.contacts.position_at", { title: contact.title })} `
                : contact.title}
              {contact.company_id != null && (
                <ReferenceField
                  source="company_id"
                  reference="companies"
                  link={false}
                >
                  <TextField source="name" />
                </ReferenceField>
              )}
            </span>
            {contact.nb_unread_emails ? (
              <span className="inline-flex w-fit items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary text-xs font-medium ml-1">
                <Mail className="w-3 h-3" />
                {contact.nb_unread_emails}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </Link>
  );
};
